import clsx from 'clsx';
import { BiListCheck } from 'react-icons/bi';

import {
  TASK_PRIORITIES,
  TASK_STATUSES,
  TaskPriority,
  TaskStatus,
  type TaskResponse,
} from '@/store/tasks/tasks.dto';

import { isTaskDueSoon, isTaskExpired } from '../TaskExpiringLabel/taskExpiration';

import styles from './TaskBadges.module.scss';

/**
 * Overdue and due-soon are mutually exclusive by construction — isTaskExpired wants the
 * deadline passed and isTaskDueSoon wants it still ahead — so a task carries at most
 * one. Both predicates read time_slot unguarded, hence the check: the schedule surfaces
 * already fall back to `deadline`, so the field does go missing.
 */
const dueBadge = (task: TaskResponse) => {
  if (!task.time_slot?.end) return null;
  if (isTaskExpired(task)) {
    return <span className={clsx(styles.badge, styles.overdue)}>Overdue</span>;
  }
  if (isTaskDueSoon(task)) {
    return <span className={clsx(styles.badge, styles.dueSoon)}>Due Soon</span>;
  }
  return null;
};

/** Steps done out of steps. Read loosely: nothing checks a list payload against the type. */
const checklistBadge = (task: TaskResponse) => {
  if (!task.checklist?.length) return null;
  const total = task.checklist.length;
  const done = task.checklist.filter((item) => item.status === 'done').length;
  return (
    <span className={clsx(styles.badge, styles.checklist)} title={`${done} of ${total} steps done`}>
      <BiListCheck size={14} aria-hidden="true" />
      {done}/{total}
    </span>
  );
};

/**
 * The markers that ride next to a maintenance task's title: where it came from, how far
 * its checklist has got, and whether its deadline needs attention. One component so the
 * desktop table's Item cell and the mobile card can't drift on which badges show or what
 * they look like.
 */
export const TaskBadges = ({ task }: { task: TaskResponse }) => (
  <>
    {task.is_ai_generated && <span className={clsx(styles.badge, styles.ai)}>AI</span>}
    {checklistBadge(task)}
    {dueBadge(task)}
  </>
);

// Labels and tones mirror TaskStatusesBadge's owner config. The statuses it omits
// (on_hold, canceled, denied, failed) render nothing there and nothing here.
const statusLabel = (status: TaskStatus) =>
  TASK_STATUSES.find((option) => option.id === status)?.label ?? status;

const STATUS_BADGES: Partial<Record<TaskStatus, { label: string; tone: string }>> = {
  [TaskStatus.PENDING]: { label: statusLabel(TaskStatus.PENDING), tone: styles.statusTodo },
  [TaskStatus.IN_PROGRESS]: {
    label: statusLabel(TaskStatus.IN_PROGRESS),
    tone: styles.statusInProgress,
  },
  [TaskStatus.ON_APPROVAL]: {
    label: statusLabel(TaskStatus.ON_APPROVAL),
    tone: styles.statusOnReview,
  },
  [TaskStatus.COMPLETED]: { label: statusLabel(TaskStatus.COMPLETED), tone: styles.statusDone },
};

/**
 * The read-only form of the status chip, shaped like the badges above rather than like
 * TaskStatusesBadge's dropdown. For surfaces that report status without offering to
 * change it — the mobile schedule card, where the whole card is a tap target and a
 * nested control would be competing for the same tap.
 */
export const StatusBadge = ({ status }: { status: TaskStatus }) => {
  const config = STATUS_BADGES[status];
  if (!config) return null;
  return <span className={clsx(styles.badge, config.tone)}>{config.label}</span>;
};

/* Priority tones, the prototype's neutral / warning / danger ramp. Labels come from
   TASK_PRIORITIES so the pill and the form's select can never disagree on wording. */
const PRIORITY_TONES: Record<TaskPriority, string> = {
  [TaskPriority.LOW]: styles.priorityLow,
  [TaskPriority.MEDIUM]: styles.priorityMedium,
  [TaskPriority.HIGH]: styles.priorityHigh,
};

const priorityLabel = (priority: TaskPriority) =>
  TASK_PRIORITIES.find((option) => option.id === priority)?.label ?? priority;

/**
 * `TaskResponse.priority` is typed as a free string and older records can carry
 * anything, so an unrecognised or empty value renders nothing — DataTable then shows its
 * own empty-cell placeholder rather than a pill inventing a level the record never set.
 */
export const PriorityBadge = ({
  priority,
  className,
}: {
  priority?: string;
  /**
   * Reshapes the pill for a surface whose other labels are a different size — the task
   * modal's header, where status / AI / overdue are all 14px on an 8px radius. The tone
   * still comes from here, so only the box changes.
   */
  className?: string;
}) => {
  const id = (priority ?? '').toLowerCase() as TaskPriority;
  const tone = PRIORITY_TONES[id];
  if (!tone) return null;
  return <span className={clsx(styles.badge, tone, className)}>{priorityLabel(id)}</span>;
};
